"use client";

import { makeArray, safeParseNumber, VehicleFilter } from "@alba-cars/common-modules";
import { useCallback, useEffect, useMemo, useReducer } from "react";

import { fuelTypeOptions } from "./_constants";
import { useVehicleFiltersData } from "../_hooks/useVehicleFilterData";
import { useFilterContext } from "./context/FilterContext";
import { useMobileSidebar } from "./context/MobileSidebarContext";
import { pendingFiltersReducer } from "./context/pendingFiltersReducer";
import { EMIFilter } from "./EMIFilter";
import FilterDropdownFullScreen from "./FullScreenDialog";
import MileageInput from "./MileageInput";
import PriceFilter from "./PriceFilter";
import StockNumberInput from "./StockNumberInput";
import { BodyTypeOption, MakeOption, ModelOption, OmittedVehicleGetDTO } from "./types";
import { YearFilter } from "./YearFilter";
import FilterDropdown from "../../../components/ui/FilterDropdown";
import IconFilter from "@/components/Icons/IconFilter";
import { getValueOrFirstOption, hasDefinedValues } from "@/utils/utils";

export default function MobileFilterSidebar() {
  const { queryOptions, resetQuery, getCurrentFilters, updateFilters } = useFilterContext();

  const {
    makesOptions,
    modelsOptions,
    featuresOptions,
    bodyTypeOptions,
    isMakesOptionsLoading,
    isModelsOptionsLoading,
    isFeaturesOptionsLoading,
    isBodyTypeOptionsLoading,
    makesLookup,
    modelsLookup,
    bodyTypeLookup,
    featuresLookup,
    fuelTypeLookup,
    setMakes: setSelectedMakes,
  } = useVehicleFiltersData();

  const [pendingFilters, dispatch] = useReducer<OmittedVehicleGetDTO, [any]>(pendingFiltersReducer, queryOptions);

  const updatePendingFilters = useCallback(
    (payload?: Partial<VehicleFilter>) => {
      dispatch({ type: "update", payload });
    },
    [dispatch],
  );

  const handleFilterChange = useCallback(
    (payload?: Partial<VehicleFilter>, callback?: Function) => {
      updatePendingFilters(payload);
      callback?.();
    },
    [updatePendingFilters],
  );

  const selectedFeatures = useMemo(() => {
    if (!pendingFilters.filter?.features?.length) return null;
    return makeArray(pendingFilters.filter.features)
      .map((featureId) => featuresLookup.get(getValueOrFirstOption(featureId)))
      .filter(Boolean);
  }, [pendingFilters.filter?.features, featuresLookup]);

  const selectedMakes = useMemo(() => {
    if (!pendingFilters.filter?.make?.length) return null;
    return makeArray(pendingFilters.filter.make)
      .map((makeId) => makesLookup.get(getValueOrFirstOption(makeId)))
      .filter(Boolean);
  }, [pendingFilters.filter?.make, makesLookup]);

  const selectedModels = useMemo(() => {
    if (!pendingFilters.filter?.model?.length) return null;
    return makeArray(pendingFilters.filter.model)
      .map((modelId) => modelsLookup.get(getValueOrFirstOption(modelId)))
      .filter(Boolean);
  }, [pendingFilters.filter?.model, modelsLookup]);

  const selectedBodyTypes = useMemo(() => {
    if (!pendingFilters.filter?.bodyType?.length) return null;
    return makeArray(pendingFilters.filter.bodyType)
      .map((bodyTypeId) => bodyTypeLookup.get(getValueOrFirstOption(bodyTypeId)))
      .filter(Boolean);
  }, [pendingFilters.filter?.bodyType, bodyTypeLookup]);

  const { isOpen, closeSidebar, openSidebar } = useMobileSidebar();

  useEffect(() => {
    const preventPageScroll = (openModalState: boolean) => {
      if (openModalState) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    };

    preventPageScroll(isOpen);

    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const applyFilters = useCallback(() => {
    const currentFilters = getCurrentFilters();
    const updatedFilter = new VehicleFilter();
    Object.assign(updatedFilter, currentFilters, pendingFilters.filter);

    updateFilters(updatedFilter);
    closeSidebar();
    !!window && window.scrollTo({ top: 0, behavior: "smooth" });
  }, [getCurrentFilters, pendingFilters.filter, updateFilters, closeSidebar]);

  const handleReset = useCallback(() => {
    dispatch({ type: "reset" });
    resetQuery();
    setSelectedMakes(undefined);
    closeSidebar();
    !!window && window.scrollTo({ top: 0, behavior: "smooth" });
  }, [resetQuery, setSelectedMakes, closeSidebar]);

  return (
    <>
      <button
        onClick={openSidebar}
        className="inter relative flex w-full items-center justify-center gap-2 rounded-2xl bg-white p-3 font-semibold lg:hidden"
        aria-label="Open filter sidebar"
      >
        <span aria-hidden="true">
          <IconFilter />
        </span>
        <span className="inter hidden font-semibold text-primary-foreground sm:block">Filter</span>
        {hasDefinedValues(pendingFilters.filter as unknown as Record<string, unknown>) && (
          <span className="absolute -top-1 right-1 h-3 w-3 rounded-full bg-primary" />
        )}
      </button>

      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50"
          aria-hidden="true"
          aria-label="Filter overlay"
          onClick={closeSidebar}
        />
      )}

      <aside aria-label="Filter options">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            applyFilters();
          }}
        >
          <div
            className={`w-[100vw - 300px] fixed left-0 top-0 z-100000 h-full transform bg-white transition-transform duration-300 ease-in-out ${
              isOpen ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div className="flex h-full flex-col overflow-y-auto px-4 pt-4">
              <div className="mb-4 flex items-center justify-between">
                <h2 className="urbanist text-[24px] font-semibold">Filters</h2>
                <button
                  onClick={closeSidebar}
                  type="button"
                  className="rounded-20 bg-[#345A8C1A] px-5 py-4 text-[#000000]"
                >
                  ✕
                </button>
              </div>
              <div className="flex-grow">
                <div className="p-1">
                  <FilterDropdownFullScreen
                    id="make-filter"
                    options={makesOptions ?? []}
                    isLoading={isMakesOptionsLoading}
                    helperText="Make"
                    onSelect={(options) =>
                      handleFilterChange({ make: options.map((option: MakeOption) => option.slug) }, () => {
                        setSelectedMakes(options.map((option: MakeOption) => option.value));
                      })
                    }
                    onClear={() =>
                      handleFilterChange({ make: undefined, model: undefined }, () => {
                        setSelectedMakes(undefined);
                      })
                    }
                    customNoOptionsMessage={"No available makes match your search"}
                    value={selectedMakes as any}
                    isMulti
                  />
                </div>
                {selectedMakes && (
                  <div className="mt-2 p-1">
                    <FilterDropdownFullScreen
                      id="model-filter"
                      options={modelsOptions ?? []}
                      isLoading={isModelsOptionsLoading}
                      helperText="Model"
                      onSelect={(options) =>
                        handleFilterChange({ model: options.map((option: ModelOption) => option.slug) })
                      }
                      onClear={() => handleFilterChange({ model: undefined })}
                      value={selectedModels as any}
                      customNoOptionsMessage={"Please select a make first "}
                      customOption={(option: unknown) => (
                        <div className="inter flex items-center gap-2">
                          <span className="text-base font-medium">{(option as ModelOption).make.name}</span>
                          <span className="text-base font-medium">-</span>
                          <span className="text-base font-medium">{(option as ModelOption).label}</span>
                        </div>
                      )}
                      isMulti
                    />
                  </div>
                )}
                <div className="mt-2 p-1">
                  <FilterDropdownFullScreen
                    id="body-type-filter"
                    options={bodyTypeOptions ?? []}
                    isLoading={isBodyTypeOptionsLoading}
                    helperText="Body Type"
                    onSelect={(options) => {
                      handleFilterChange({ bodyType: options.map((option: BodyTypeOption) => option.slug) });
                    }}
                    customNoOptionsMessage={"No available body types match your search"}
                    onClear={() => handleFilterChange({ bodyType: undefined })}
                    value={selectedBodyTypes as any}
                    isMulti
                  />
                </div>
                <div className="mt-4 p-1">
                  <PriceFilter
                    onFromPriceChange={(fromPrice) => {
                      const fromPriceValue = (fromPrice as unknown as { value: number; label: string })?.value;
                      handleFilterChange({
                        price: { ...(pendingFilters.filter?.price ?? {}), min: fromPriceValue },
                      });
                    }}
                    onFromPriceClear={() => {
                      handleFilterChange({
                        price: { ...(pendingFilters.filter?.price ?? {}), min: undefined },
                      });
                    }}
                    onToPriceChange={(toPrice) => {
                      const toPriceValue = (toPrice as unknown as { value: number; label: string })?.value;
                      handleFilterChange({
                        price: { ...(pendingFilters.filter?.price ?? {}), max: toPriceValue },
                      });
                    }}
                    onToPriceClear={() => {
                      handleFilterChange({
                        price: { ...(pendingFilters.filter?.price ?? {}), max: undefined },
                      });
                    }}
                    onReset={() => handleFilterChange({ price: undefined })}
                    fromValue={
                      pendingFilters.filter?.price?.min
                        ? { value: pendingFilters.filter.price.min, label: pendingFilters.filter.price.min.toString() }
                        : null
                    }
                    toValue={
                      pendingFilters.filter?.price?.max
                        ? { value: pendingFilters.filter.price.max, label: pendingFilters.filter.price.max.toString() }
                        : null
                    }
                  />
                </div>
                <div className="mt-4 p-1">
                  <EMIFilter
                    onFromEMIChange={(fromPrice) => {
                      const fromEMIValue = (fromPrice as unknown as { value: number; label: string })?.value;
                      handleFilterChange({
                        emi: { ...(pendingFilters.filter?.emi ?? {}), min: Math.round(fromEMIValue) },
                      });
                    }}
                    onFromEMIClear={() => {
                      handleFilterChange({
                        emi: { ...(pendingFilters.filter?.emi ?? {}), min: undefined },
                      });
                    }}
                    onToEMIChange={(toPrice) => {
                      const toEMIValue = (toPrice as unknown as { value: number; label: string })?.value;
                      handleFilterChange({
                        emi: { ...(pendingFilters.filter?.emi ?? {}), max: Math.round(toEMIValue) },
                      });
                    }}
                    onToEMIClear={() => {
                      handleFilterChange({
                        emi: { ...(pendingFilters.filter?.emi ?? {}), max: undefined },
                      });
                    }}
                    onReset={() => handleFilterChange({ emi: undefined })}
                    fromValue={
                      pendingFilters.filter?.emi?.min
                        ? {
                            value: pendingFilters.filter.emi.min,
                            label: pendingFilters.filter.emi.min.toString(),
                          }
                        : null
                    }
                    toValue={
                      pendingFilters.filter?.emi?.max
                        ? {
                            value: pendingFilters.filter.emi.max,
                            label: pendingFilters.filter.emi.max.toString(),
                          }
                        : null
                    }
                  />
                </div>
                <div className="mt-4 p-1">
                  <YearFilter
                    onFromYearChange={(fromYear) => {
                      const fromYearValue = (fromYear as unknown as { value: number; label: string })?.value;
                      handleFilterChange({
                        year: { ...(pendingFilters.filter?.year ?? {}), min: Math.round(fromYearValue) },
                      });
                    }}
                    onFromYearClear={() => {
                      handleFilterChange({
                        year: { ...(pendingFilters.filter?.year ?? {}), min: undefined },
                      });
                    }}
                    onToYearChange={(toYear) => {
                      const toYearValue = (toYear as unknown as { value: number; label: string })?.value;
                      handleFilterChange({
                        year: { ...(pendingFilters.filter?.year ?? {}), max: Math.round(toYearValue) },
                      });
                    }}
                    onToYearClear={() => {
                      handleFilterChange({
                        year: { ...(pendingFilters.filter?.year ?? {}), max: undefined },
                      });
                    }}
                    onReset={() => handleFilterChange({ year: undefined })}
                    fromValue={
                      pendingFilters.filter?.year?.min
                        ? { value: pendingFilters.filter.year.min, label: pendingFilters.filter.year.min.toString() }
                        : null
                    }
                    toValue={
                      pendingFilters.filter?.year?.max
                        ? { value: pendingFilters.filter.year.max, label: pendingFilters.filter.year.max.toString() }
                        : null
                    }
                  />
                </div>
                <div className="mt-4 p-1">
                  <MileageInput
                    onFromMileageChange={(value) => {
                      handleFilterChange({
                        mileage: { ...(pendingFilters.filter?.mileage ?? {}), min: safeParseNumber(value) },
                      });
                    }}
                    onToMileageChange={(value) => {
                      handleFilterChange({
                        mileage: { ...(pendingFilters.filter?.mileage ?? {}), max: safeParseNumber(value) },
                      });
                    }}
                    onFromMileageClear={() => {
                      handleFilterChange({
                        mileage: { ...(pendingFilters.filter?.mileage ?? {}), min: undefined },
                      });
                    }}
                    onToMileageClear={() => {
                      handleFilterChange({
                        mileage: { ...(pendingFilters.filter?.mileage ?? {}), max: undefined },
                      });
                    }}
                    onReset={() => handleFilterChange({ mileage: undefined })}
                    fromValue={
                      pendingFilters.filter?.mileage?.min ? pendingFilters.filter.mileage.min.toString() : null
                    }
                    toValue={pendingFilters.filter?.mileage?.max ? pendingFilters.filter.mileage.max.toString() : null}
                  />
                </div>
                <div className="mt-4 p-1">
                  <FilterDropdown
                    id="fuel-type-filter"
                    options={fuelTypeOptions}
                    helperText="Fuel Type"
                    isSearchable={false}
                    withLabel={true}
                    onSelect={(option) => handleFilterChange({ fuelType: option.value })}
                    onClear={() => handleFilterChange({ fuelType: undefined })}
                    value={
                      pendingFilters?.filter?.fuelType
                        ? (fuelTypeLookup.get(getValueOrFirstOption(pendingFilters.filter.fuelType)) ?? null)
                        : null
                    }
                    customClassNames={{
                      menu: () =>
                        "mt-1 !mb-2 !border !border-secondary-light !rounded-2xl !bg-white !shadow-lg !relative !top-full !left-0 !w-full !z-50",
                      menuList: () => "inter !font-medium !max-h-[250px] !overflow-y-scroll !no-scrollbar",
                    }}
                  />
                </div>
                <div className="mt-4 p-1">
                  <FilterDropdownFullScreen
                    id="features-filter"
                    options={featuresOptions ?? []}
                    isLoading={isFeaturesOptionsLoading}
                    helperText="Features"
                    withLabel={true}
                    onSelect={(options) => {
                      const selectedOptions: { value: string; label: string }[] = options;
                      handleFilterChange({
                        features: selectedOptions.map((option) => option.value),
                      });
                    }}
                    onClear={() => handleFilterChange({ features: undefined })}
                    value={selectedFeatures as any}
                    isMulti
                  />
                </div>
                <div className="mt-4 p-1">
                  <StockNumberInput
                    onValueChange={(value) => {
                      handleFilterChange({ chassisNumber: value });
                    }}
                    onReset={() => handleFilterChange({ chassisNumber: undefined })}
                    value={
                      Array.isArray(pendingFilters.filter?.chassisNumber)
                        ? pendingFilters.filter?.chassisNumber[0]
                        : pendingFilters.filter?.chassisNumber
                    }
                    noDebounce
                  />
                </div>
              </div>
              {/* Buttons */}
              <div className="sticky bottom-0 rounded-2xl bg-white py-3 [&:not(:hover)]:shadow-none">
                <div className="flex gap-2">
                  <button
                    className="inter w-1/2 rounded-[16px] bg-primary-light px-4 py-3 text-center font-semibold text-primary"
                    onClick={handleReset}
                    aria-label="Reset all filters"
                    type="button"
                  >
                    Reset All
                  </button>
                  <button
                    className="inter w-1/2 flex-grow rounded-[16px] bg-primary px-4 py-3 text-center font-semibold text-white"
                    onClick={applyFilters}
                    aria-label="Apply filters"
                  >
                    Show
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      </aside>
    </>
  );
}
